import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { storesAPI, exportAPI, downloadBlob, geoAPI, utilsAPI, uploadToCloudinary } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LocationMap } from '../components/LocationMap';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Download,
  Store,
  Phone,
  MapPin,
  FileSpreadsheet,
  FileText,
  Loader2,
  Navigation,
  Crosshair
} from 'lucide-react';

const INITIAL_FORM = {
  name: '',
  fantasy_name: '',
  cnpj: '',
  inscricao_estadual: '',
  responsible_name: '',
  owner_cpf: '',
  email: '',
  phone: '',
  whatsapp: '',
  // Endereço detalhado
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  address: '',
  city: '',
  state: '',
  // Financeiro
  pix_key: '',
  pix_type: '',
  pix_holder: '',
  bank_name: '',
  payment_methods: [],
  // Funcionamento
  operating_hours: '',
  operating_days: [],
  facade_url: '',
  logo_url: '',
  banner_url: '',
  // Institucional (Etapa 1)
  owner_photo_url: '',
  history: '',
  mission: '',
  description: '',
  instagram: '',
  facebook: '',
  website: '',
  verified: false,
  marketing_photos: [],
  marketing_videos: [],
  // Contrato
  contract_number: '',
  contract_start: '',
  contract_end: '',
  contract_status: 'ativo',
  lat: null,
  lng: null,
};

const PAYMENT_METHODS_OPTIONS = [
  'PIX', 'Cartão de Crédito', 'Cartão de Débito', 'Dinheiro', 'Boleto', 'Transferência', 'Vale-Alimentação'
];
const WEEK_DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

// Small local uploaders (single / multi image / multi video) to avoid touching global components
const SingleImageUploader = ({ label, value, onChange, testId, folder }) => {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file, 'image', folder);
      onChange(url);
      toast.success('Imagem enviada.');
    } catch (_) {
      toast.error('Falha ao enviar imagem.');
    } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        {value && (
          <img src={value} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
        )}
        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm hover:bg-slate-50" data-testid={testId}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Selecionar'}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange('')} className="text-xs text-red-600 hover:underline">Remover</button>
        )}
      </div>
    </div>
  );
};

const MultiImageUploader = ({ label, value = [], onChange, testId, folder }) => {
  const [uploading, setUploading] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const u = await uploadToCloudinary(f, 'image', folder);
        urls.push(u);
      }
      onChange([...(value || []), ...urls]);
      toast.success(`${urls.length} imagem(ns) enviada(s).`);
    } catch (_) { toast.error('Falha em algum upload.'); } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {(value || []).map((u, i) => (
          <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
            <img src={u} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs">×</button>
          </div>
        ))}
        <label className="cursor-pointer w-20 h-20 rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-xs text-slate-500 hover:border-blue-400" data-testid={testId}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : '+ Adicionar'}
          <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading} />
        </label>
      </div>
    </div>
  );
};

const MultiVideoUploader = ({ label, value = [], onChange, testId, folder }) => {
  const [uploading, setUploading] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const u = await uploadToCloudinary(f, 'video', folder);
        urls.push(u);
      }
      onChange([...(value || []), ...urls]);
      toast.success(`${urls.length} vídeo(s) enviado(s).`);
    } catch (_) { toast.error('Falha ao enviar vídeo.'); } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {(value || []).map((u, i) => (
          <div key={i} className="relative w-32 h-20 rounded-lg overflow-hidden border border-slate-200 bg-black">
            <video src={u} className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs">×</button>
          </div>
        ))}
        <label className="cursor-pointer w-32 h-20 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-500 hover:border-blue-400" data-testid={testId}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : '+ Vídeo'}
          <input type="file" accept="video/*" className="hidden" onChange={onFiles} disabled={uploading} />
        </label>
      </div>
    </div>
  );
};

const PIX_TYPES = ['CPF', 'CNPJ', 'E-mail', 'Telefone', 'Aleatória'];

const STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO'
];

export const Stores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [storeToDelete, setStoreToDelete] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadStores();
  }, [search, statusFilter]);

  const loadStores = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      const { data } = await storesAPI.list(params);
      setStores(data);
    } catch (error) {
      toast.error('Erro ao carregar lojas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Recompose full address string from individual fields at submit time
      const composedAddress = [
        formData.street,
        formData.number,
        formData.complement,
        formData.neighborhood,
        formData.city,
        formData.state,
      ].filter(Boolean).join(', ') || formData.address;
      const payload = { ...formData, address: composedAddress };

      if (editingStore) {
        await storesAPI.update(editingStore.id, payload);
        toast.success('Loja atualizada com sucesso!');
      } else {
        await storesAPI.create(payload);
        toast.success('Loja cadastrada com sucesso!');
      }
      setDialogOpen(false);
      setFormData(INITIAL_FORM);
      setEditingStore(null);
      loadStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar loja');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name || '',
      fantasy_name: store.fantasy_name || '',
      cnpj: store.cnpj || '',
      inscricao_estadual: store.inscricao_estadual || '',
      responsible_name: store.responsible_name || '',
      owner_cpf: store.owner_cpf || '',
      email: store.email || '',
      phone: store.phone || '',
      whatsapp: store.whatsapp || '',
      cep: store.cep || '',
      street: store.street || '',
      number: store.number || '',
      complement: store.complement || '',
      neighborhood: store.neighborhood || '',
      address: store.address || '',
      city: store.city || '',
      state: store.state || '',
      pix_key: store.pix_key || '',
      pix_type: store.pix_type || '',
      pix_holder: store.pix_holder || '',
      bank_name: store.bank_name || '',
      payment_methods: store.payment_methods || [],
      operating_hours: store.operating_hours || '',
      operating_days: store.operating_days || [],
      facade_url: store.facade_url || '',
      logo_url: store.logo_url || '',
      banner_url: store.banner_url || '',
      owner_photo_url: store.owner_photo_url || '',
      history: store.history || '',
      mission: store.mission || '',
      description: store.description || '',
      instagram: store.instagram || '',
      facebook: store.facebook || '',
      website: store.website || '',
      verified: !!store.verified,
      marketing_photos: store.marketing_photos || [],
      marketing_videos: store.marketing_videos || [],
      contract_number: store.contract_number || '',
      contract_start: store.contract_start || '',
      contract_end: store.contract_end || '',
      contract_status: store.contract_status || 'ativo',
      lat: store.lat ?? null,
      lng: store.lng ?? null,
    });
    setDialogOpen(true);
  };

  const togglePayment = (method) => {
    setFormData((f) => ({
      ...f,
      payment_methods: f.payment_methods.includes(method)
        ? f.payment_methods.filter((m) => m !== method)
        : [...f.payment_methods, method],
    }));
  };

  const toggleDay = (day) => {
    setFormData((f) => ({
      ...f,
      operating_days: f.operating_days.includes(day)
        ? f.operating_days.filter((d) => d !== day)
        : [...f.operating_days, day],
    }));
  };

  const handleAdminCepLookup = async () => {
    const digits = (formData.cep || '').replace(/\D/g, '');
    if (digits.length !== 8) {
      toast.error('CEP inválido — digite 8 números.');
      return;
    }
    try {
      const { data } = await utilsAPI.lookupCEP(digits);
      setFormData((f) => ({
        ...f,
        street: data.street || f.street,
        neighborhood: data.neighborhood || f.neighborhood,
        city: data.city || f.city,
        state: data.state || f.state,
        address: [data.street, f.number, data.neighborhood, data.city, data.state].filter(Boolean).join(', '),
      }));
      toast.success('Endereço preenchido pelo CEP.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não conseguimos localizar esse CEP.');
    }
  };

  const handleDelete = async () => {
    try {
      await storesAPI.delete(storeToDelete.id);
      toast.success('Loja excluída com sucesso!');
      setDeleteDialogOpen(false);
      setStoreToDelete(null);
      loadStores();
    } catch (error) {
      toast.error('Erro ao excluir loja');
    }
  };

  const handleExport = async (format) => {
    try {
      const { data } = await exportAPI.stores({ format });
      downloadBlob(data, `lojas.${format}`);
      toast.success('Exportação concluída!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      ativo: { class: 'active', label: 'Ativo' },
      vencido: { class: 'expired', label: 'Vencido' },
      suspenso: { class: 'suspended', label: 'Suspenso' }
    };
    const s = statusMap[status] || statusMap.ativo;
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <div className="space-y-6" data-testid="stores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Lojas</h1>
          <p className="text-slate-500 mt-1">Gerencie suas lojas parceiras</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileText className="w-4 h-4 mr-2" />
                CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button 
            onClick={() => { setEditingStore(null); setFormData(INITIAL_FORM); setDialogOpen(true); }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-store-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Loja
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome, CNPJ ou responsável..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="ativo">Ativo</SelectItem>
                <SelectItem value="vencido">Vencido</SelectItem>
                <SelectItem value="suspenso">Suspenso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stores Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : stores.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Store className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">Nenhuma loja encontrada</p>
              <p className="text-sm">Cadastre sua primeira loja para começar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Loja</th>
                    <th>Responsável</th>
                    <th>Cidade/UF</th>
                    <th>Contato</th>
                    <th>Contrato</th>
                    <th>Status</th>
                    <th>Pedidos</th>
                    <th>Receita</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {stores.map((store) => (
                    <tr key={store.id} data-testid={`store-row-${store.id}`}>
                      <td>
                        <div className="font-medium text-slate-900">{store.name}</div>
                        <div className="text-sm text-slate-500">{store.cnpj}</div>
                      </td>
                      <td className="text-slate-700">{store.responsible_name}</td>
                      <td>
                        <div className="flex items-center gap-1 text-slate-600">
                          <MapPin className="w-4 h-4" />
                          {store.city}/{store.state}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-4 h-4" />
                          {store.phone}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">
                          <div className="text-slate-700">#{store.contract_number}</div>
                          <div className="text-slate-500">
                            {new Date(store.contract_start).toLocaleDateString('pt-BR')} - {new Date(store.contract_end).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                      </td>
                      <td>{getStatusBadge(store.contract_status)}</td>
                      <td className="text-center font-medium">{store.total_orders || 0}</td>
                      <td className="font-medium text-green-600">{formatCurrency(store.total_revenue)}</td>
                      <td>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`store-menu-${store.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/admin/lojas/${store.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver detalhes
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(store)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => { setStoreToDelete(store); setDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStore ? 'Editar Loja' : 'Nova Loja'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section 1: Dados da Empresa */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 border-b border-blue-100 pb-1">Dados da Empresa</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Razão Social *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="input-store-name" />
                </div>
                <div className="space-y-2">
                  <Label>Nome Fantasia</Label>
                  <Input value={formData.fantasy_name} onChange={(e) => setFormData({ ...formData, fantasy_name: e.target.value })} data-testid="input-fantasy-name" />
                </div>
                <div className="space-y-2">
                  <Label>CNPJ *</Label>
                  <Input value={formData.cnpj} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} placeholder="00.000.000/0000-00" required data-testid="input-cnpj" />
                </div>
                <div className="space-y-2">
                  <Label>Inscrição Estadual</Label>
                  <Input value={formData.inscricao_estadual} onChange={(e) => setFormData({ ...formData, inscricao_estadual: e.target.value })} data-testid="input-ie" />
                </div>
                <div className="space-y-2">
                  <Label>Nome do Proprietário *</Label>
                  <Input value={formData.responsible_name} onChange={(e) => setFormData({ ...formData, responsible_name: e.target.value })} required data-testid="input-responsible" />
                </div>
                <div className="space-y-2">
                  <Label>CPF do Proprietário</Label>
                  <Input value={formData.owner_cpf} onChange={(e) => setFormData({ ...formData, owner_cpf: e.target.value })} placeholder="000.000.000-00" data-testid="input-owner-cpf" />
                </div>
                <div className="space-y-2">
                  <Label>E-mail *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="contato@loja.com" required data-testid="input-email" />
                </div>
                <div className="space-y-2">
                  <Label>Telefone *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(00) 0000-0000" required data-testid="input-phone" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>WhatsApp *</Label>
                  <Input value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} placeholder="(00) 00000-0000" required data-testid="input-whatsapp" />
                </div>
              </div>
            </section>

            {/* Section 2: Endereço Completo */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 border-b border-blue-100 pb-1">Endereço Completo</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CEP *</Label>
                  <div className="flex gap-2">
                    <Input value={formData.cep} onChange={(e) => setFormData({ ...formData, cep: e.target.value })} onBlur={() => (formData.cep || '').replace(/\D/g, '').length === 8 && handleAdminCepLookup()} placeholder="00000-000" required data-testid="input-cep" />
                    <Button type="button" variant="outline" onClick={handleAdminCepLookup}>Buscar</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Estado *</Label>
                  <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                    <SelectTrigger data-testid="select-state"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Rua *</Label>
                  <Input value={formData.street} onChange={(e) => setFormData({ ...formData, street: e.target.value, address: [e.target.value, formData.number, formData.neighborhood, formData.city, formData.state].filter(Boolean).join(', ') })} required data-testid="input-street" />
                </div>
                <div className="space-y-2">
                  <Label>Número *</Label>
                  <Input value={formData.number} onChange={(e) => setFormData({ ...formData, number: e.target.value })} required data-testid="input-number" />
                </div>
                <div className="space-y-2">
                  <Label>Complemento</Label>
                  <Input value={formData.complement} onChange={(e) => setFormData({ ...formData, complement: e.target.value })} data-testid="input-complement" />
                </div>
                <div className="space-y-2">
                  <Label>Bairro *</Label>
                  <Input value={formData.neighborhood} onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })} required data-testid="input-neighborhood" />
                </div>
                <div className="space-y-2">
                  <Label>Cidade *</Label>
                  <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required data-testid="input-city" />
                </div>
              </div>
              <StoreMapPicker
                lat={formData.lat} lng={formData.lng} address={formData.address}
                city={formData.city} state={formData.state} cep={formData.cep}
                onChange={(lat, lng) => setFormData({ ...formData, lat, lng })}
              />
            </section>

            {/* Section 3: Dados Financeiros */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 border-b border-blue-100 pb-1">Dados Financeiros</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Chave PIX</Label>
                  <Input value={formData.pix_key} onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })} placeholder="email, CPF, CNPJ ou aleatória" data-testid="input-pix-key" />
                </div>
                <div className="space-y-2">
                  <Label>Tipo da Chave PIX</Label>
                  <Select value={formData.pix_type} onValueChange={(v) => setFormData({ ...formData, pix_type: v })}>
                    <SelectTrigger data-testid="select-pix-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{PIX_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nome do Favorecido</Label>
                  <Input value={formData.pix_holder} onChange={(e) => setFormData({ ...formData, pix_holder: e.target.value })} placeholder="Nome que aparece no PIX" data-testid="input-pix-holder" />
                </div>
                <div className="space-y-2">
                  <Label>Banco</Label>
                  <Input value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} placeholder="Ex.: Banco do Brasil" data-testid="input-bank" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Formas de Pagamento Aceitas</Label>
                <div className="flex flex-wrap gap-2">
                  {PAYMENT_METHODS_OPTIONS.map((m) => {
                    const active = formData.payment_methods.includes(m);
                    return (
                      <button
                        key={m} type="button" onClick={() => togglePayment(m)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-400'}`}
                        data-testid={`payment-method-${m.toLowerCase().replace(/\s+/g,'-')}`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Section 4: Funcionamento */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 border-b border-blue-100 pb-1">Funcionamento</h3>
              <div className="space-y-2">
                <Label>Horário de Funcionamento</Label>
                <Input value={formData.operating_hours} onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })} placeholder="Ex.: 08:00 - 18:00" data-testid="input-hours" />
              </div>
              <div className="space-y-2">
                <Label>Dias de Funcionamento</Label>
                <div className="flex flex-wrap gap-2">
                  {WEEK_DAYS.map((d) => {
                    const active = formData.operating_days.includes(d);
                    return (
                      <button
                        key={d} type="button" onClick={() => toggleDay(d)}
                        className={`w-11 h-11 rounded-lg text-xs font-bold border transition-colors ${active ? 'bg-amber-400 text-blue-900 border-amber-400' : 'bg-white text-slate-500 border-slate-200 hover:border-amber-400'}`}
                        data-testid={`day-${d.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Section: Página Institucional */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 border-b border-blue-100 pb-1">
                Página Institucional
              </h3>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
                <input
                  id="verified"
                  type="checkbox"
                  checked={!!formData.verified}
                  onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
                  className="w-5 h-5"
                  data-testid="input-verified"
                />
                <label htmlFor="verified" className="text-sm font-semibold text-blue-800">
                  Selo &quot;Loja Verificada DESTACK&quot;
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SingleImageUploader label="Logo da loja" value={formData.logo_url}
                  onChange={(url) => setFormData({ ...formData, logo_url: url })}
                  testId="upload-store-logo" folder="stores/logo" />
                <SingleImageUploader label="Banner institucional" value={formData.banner_url}
                  onChange={(url) => setFormData({ ...formData, banner_url: url })}
                  testId="upload-store-banner" folder="stores/banner" />
                <SingleImageUploader label="Foto do proprietário/equipe" value={formData.owner_photo_url}
                  onChange={(url) => setFormData({ ...formData, owner_photo_url: url })}
                  testId="upload-store-owner" folder="stores/owner" />
              </div>
              <SingleImageUploader label="Foto da fachada (opcional)" value={formData.facade_url}
                onChange={(url) => setFormData({ ...formData, facade_url: url })}
                testId="upload-store-facade" folder="stores/facade" />
              <div className="space-y-2">
                <Label>Descrição curta</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Frase curta que descreve a loja" data-testid="input-description" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nossa História</Label>
                  <textarea rows={4} value={formData.history}
                    onChange={(e) => setFormData({ ...formData, history: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 p-2 text-sm"
                    placeholder="Conte a história da loja..."
                    data-testid="input-history"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Missão</Label>
                  <textarea rows={4} value={formData.mission}
                    onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 p-2 text-sm"
                    placeholder="Missão e valores..."
                    data-testid="input-mission"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Instagram</Label>
                  <Input value={formData.instagram} onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    placeholder="@loja ou URL" data-testid="input-instagram" />
                </div>
                <div className="space-y-2">
                  <Label>Facebook</Label>
                  <Input value={formData.facebook} onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    placeholder="URL ou usuário" data-testid="input-facebook" />
                </div>
                <div className="space-y-2">
                  <Label>Site</Label>
                  <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="www.exemplo.com.br" data-testid="input-website" />
                </div>
              </div>
              <MultiImageUploader label="Galeria de marketing (fotos)"
                value={formData.marketing_photos}
                onChange={(arr) => setFormData({ ...formData, marketing_photos: arr })}
                testId="upload-marketing-photos" folder="stores/marketing" />
              <MultiVideoUploader label="Vídeos institucionais"
                value={formData.marketing_videos}
                onChange={(arr) => setFormData({ ...formData, marketing_videos: arr })}
                testId="upload-marketing-videos" folder="stores/marketing-videos" />
            </section>

            {/* Section 5: Contrato */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 border-b border-blue-100 pb-1">Contrato</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número do Contrato *</Label>
                  <Input value={formData.contract_number} onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })} required data-testid="input-contract-number" />
                </div>
                <div className="space-y-2">
                  <Label>Status do Contrato</Label>
                  <Select value={formData.contract_status} onValueChange={(value) => setFormData({ ...formData, contract_status: value })}>
                    <SelectTrigger data-testid="select-contract-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="vencido">Vencido</SelectItem>
                      <SelectItem value="suspenso">Suspenso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data Início *</Label>
                  <Input type="date" value={formData.contract_start} onChange={(e) => setFormData({ ...formData, contract_start: e.target.value })} required data-testid="input-contract-start" />
                </div>
                <div className="space-y-2">
                  <Label>Data Fim *</Label>
                  <Input type="date" value={formData.contract_end} onChange={(e) => setFormData({ ...formData, contract_end: e.target.value })} required data-testid="input-contract-end" />
                </div>
              </div>
            </section>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={submitting} data-testid="submit-store-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingStore ? 'Salvar' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Tem certeza que deseja excluir a loja <strong>{storeToDelete?.name}</strong>?
            Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// StoreMapPicker — admin picks store coordinates: auto-geocode + manual click
const StoreMapPicker = ({ lat, lng, address, city, state, cep, onChange }) => {
  const [loading, setLoading] = useState(false);

  const handleAutoGeocode = async () => {
    if (!address || !city) {
      toast.error('Preencha endereço e cidade antes de localizar.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await geoAPI.geocode({ address, city, state, cep });
      onChange(data.lat, data.lng);
      toast.success('Posição localizada no mapa.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível localizar. Clique no mapa para marcar.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => onChange(null, null);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Navigation className="w-4 h-4 text-blue-600" />
          Localização no Mapa
        </Label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleAutoGeocode}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            data-testid="geocode-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Crosshair className="w-3.5 h-3.5" />}
            Localizar pelo endereço
          </button>
          {lat != null && (
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-slate-500 hover:text-rose-600 px-2 py-1"
              data-testid="clear-coords-btn"
            >
              Limpar
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500">
        Clique em &quot;Localizar pelo endereço&quot; ou diretamente no mapa para ajustar a posição da loja.
      </p>
      <LocationMap
        lat={lat}
        lng={lng}
        onPick={onChange}
        height="280px"
        testId="store-map-picker"
      />
      {lat != null && lng != null && (
        <p className="text-xs text-slate-500 font-mono">
          Coordenadas: {lat.toFixed(6)}, {lng.toFixed(6)}
        </p>
      )}
    </div>
  );
};
